import React, { useState, useEffect, Fragment } from "react";
import { useParams } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useDarkMode } from "../../contexts/DarkModeContext";

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label }) => {
  const { isDarkMode } = useDarkMode();

  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: isDarkMode ? "rgba(30, 41, 59, 0.95)" : "rgba(255, 255, 255, 0.9)",
          border: isDarkMode ? "1px solid rgba(71, 85, 105, 0.3)" : "1px solid rgba(255, 255, 255, 0.3)",
          borderRadius: "8px",
          padding: "12px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          color: isDarkMode ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.8)",
          fontSize: "13px",
          fontWeight: "500",
        }}
      >
        <div
          style={{
            fontWeight: "bold",
            fontSize: "14px",
            marginBottom: "4px",
            color: isDarkMode ? "rgba(255, 255, 255, 0.95)" : "rgba(0, 0, 0, 0.9)",
          }}
        >
          {new Date(label).toLocaleDateString("fr-FR", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </div>
        {payload.map((entry, index) => {
          return (
            <div key={index} style={{ marginTop: "2px" }}>
              <span style={{ color: entry.color }}>
                {`Poids: ${entry.value} kg`}
              </span>
              {entry.payload.gmq && (
                <div style={{ marginTop: "2px" }}>
                  <span style={{ color: isDarkMode ? "#a78bfa" : "#7c3aed" }}>
                    {`GMQ: ${entry.payload.gmq.toFixed(3)} kg/jour`}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }
  return null;
};

// PDF-optimized Tooltip Component (always light mode)
const PDFTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          border: "1px solid rgba(0, 0, 0, 0.2)",
          borderRadius: "4px",
          padding: "8px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          color: "rgba(0, 0, 0, 0.8)",
          fontSize: "11px",
          fontWeight: "500",
        }}
      >
        <div
          style={{
            fontWeight: "bold",
            fontSize: "12px",
            marginBottom: "2px",
            color: "rgba(0, 0, 0, 0.9)",
          }}
        >
          {new Date(label).toLocaleDateString("fr-FR", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        </div>
        {payload.map((entry, index) => {
          return (
            <div key={index} style={{ marginTop: "1px" }}>
              <span style={{ color: entry.color }}>
                {`Poids: ${entry.value} kg`}
              </span>
              {entry.payload.gmq && (
                <div style={{ marginTop: "1px" }}>
                  <span style={{ color: "#7c3aed" }}>
                    {`GMQ: ${entry.payload.gmq.toFixed(3)} kg/jour`}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }
  return null;
};

function WeightChart(props) {
  const [processedWeights, setProcessedWeights] = useState([]);
  const [GMQ, setGMQ] = useState(0);
  const [yAxisDomain, setYAxisDomain] = useState([30, 70]);
  const [yAxisTicks, setYAxisTicks] = useState([30, 40, 50, 60, 70]);
  const { isDarkMode } = useDarkMode();

  // Check if this is for PDF generation
  const isPdfMode = props.isPdfMode || false;

  function processWeights(weights) {
    if (weights.length === 0) return [];

    // Sort weights by date
    const sortedWeights = [...weights].sort((a, b) => new Date(a.x) - new Date(b.x));

    // Calculate GMQ for each weight point
    const processed = sortedWeights.map((weight, index) => {
      let gmq = 0;

      if (index > 0) {
        const currentDate = new Date(weight.x);
        const previousDate = new Date(sortedWeights[index - 1].x);
        const daysDiff = (currentDate - previousDate) / (1000 * 3600 * 24);
        const weightDiff = weight.y - sortedWeights[index - 1].y;

        if (daysDiff > 0) {
          gmq = weightDiff / daysDiff;
        }
      }

      return {
        ...weight,
        gmq: gmq
      };
    });

    // Calculate Y-axis domain and ticks dynamically
    if (sortedWeights.length > 0) {
      const maxWeight = Math.max(...sortedWeights.map(w => w.y));
      const minWeight = Math.min(...sortedWeights.map(w => w.y));

      // Start at 30, end at next multiple of 10 above max weight
      const maxDomain = Math.ceil(maxWeight / 10) * 10;
      const domain = [30, maxDomain];

      // Generate ticks from 30 to maxDomain in steps of 10
      const ticks = [];
      for (let i = 30; i <= maxDomain; i += 10) {
        ticks.push(i);
      }

      setYAxisDomain(domain);
      setYAxisTicks(ticks);
    }

    // Calculate overall GMQ (from first to last weight)
    if (sortedWeights.length > 1) {
      const firstWeight = sortedWeights[0];
      const lastWeight = sortedWeights[sortedWeights.length - 1];
      const totalDays = (new Date(lastWeight.x) - new Date(firstWeight.x)) / (1000 * 3600 * 24);
      const totalWeightDiff = lastWeight.y - firstWeight.y;

      if (totalDays > 0) {
        const overallGMQ = totalWeightDiff / totalDays;
        setGMQ(overallGMQ);
        if (props.setGMQ != undefined) {
          props.setGMQ(overallGMQ);
        }
      }
    }

    setProcessedWeights(processed);
  }

  let { id } = useParams();
  if (id === undefined) {
    id = "null";
  }

  useEffect(() => {
    processWeights(props.weights);
  }, [props]);

  // If in PDF mode, use the original inline styles
  if (isPdfMode) {
    const chartStyles = {
      container: {
        border: 'none',
        backgroundColor: 'transparent',
        padding: '0',
        margin: '0'
      },
      title: {
        fontSize: '14px',
        fontWeight: 'bold',
        color: '#1f2937',
        marginBottom: '10px'
      },
      chartContainer: {
        height: '200px',
        width: '100%'
      },
      gridColor: '#e5e7eb',
      textColor: '#374151',
      axisColor: '#d1d5db'
    };

    return (
      <Fragment>
        <div style={chartStyles.container}>
          <div style={chartStyles.title}>
            Évolution du poids du veau au cours du temps
          </div>
          <div style={chartStyles.chartContainer}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart margin={{ top: 5, right: 0, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#fcd34d" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#bfdbfe" stopOpacity={0.0} />
                  </linearGradient>
                </defs>

                <CartesianGrid vertical={false} opacity={0.2} stroke={chartStyles.gridColor} />
                <XAxis
                  dataKey="x"
                  tick={{ fill: chartStyles.textColor, fontSize: "10px" }}
                  tickLine={false}
                  axisLine={{ stroke: chartStyles.axisColor }}
                  tickFormatter={(value) => {
                    // Calculate weeks since birth (assuming first weight is birth)
                    if (processedWeights.length > 0) {
                      const birthDate = new Date(processedWeights[0].x);
                      const currentDate = new Date(value);
                      const weeksDiff = Math.floor((currentDate - birthDate) / (1000 * 3600 * 24 * 7));
                      return `${weeksDiff} sem`;
                    }
                    return "";
                  }}
                />
                <YAxis
                  tick={{ fill: chartStyles.textColor, fontSize: "10px" }}
                  axisLine={false}
                  tickLine={false}
                  domain={yAxisDomain}
                  ticks={yAxisTicks}
                />
                <Tooltip
                  content={<PDFTooltip />}
                  cursor={{ fill: "transparent" }}
                />
                <Line
                  data={processedWeights}
                  dataKey="y"
                  label="Poids"
                  type="basis"
                  dot={false}
                  stroke="#f59e0b"
                  fill="url(#colorValue)"
                  strokeWidth={2}
                  radius={[7, 7, 0, 0]}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <div className="mb-3 flex w-full flex-col rounded-xl border border-gray-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
        <div className="mb-6">
          <span className="font-semibold dark:text-white">
            Évolution du poids du veau au cours du temps
          </span>
        </div>
        <div className="h-80 w-full md:px-3">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart margin={{ top: 5, right: 0, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                  {isDarkMode ? (
                    <>
                      <stop offset="5%" stopColor="#fcd34d" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#172554" stopOpacity={0.0} />
                    </>
                  ) : (
                    <>
                      <stop offset="5%" stopColor="#fcd34d" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#bfdbfe" stopOpacity={0.0} />
                    </>
                  )}
                </linearGradient>
              </defs>

              <CartesianGrid vertical={false} opacity={isDarkMode ? 0.1 : 0.2} stroke={isDarkMode ? "#475569" : "#e2e8f0"} />
              <XAxis
                dataKey="x"
                tick={{ fill: isDarkMode ? "#94a3b8" : "#475569", fontSize: "12px" }}
                tickLine={false}
                axisLine={{ stroke: isDarkMode ? "#475569" : "#f1f5f9" }}
                tickFormatter={(value) => {
                  // Calculate weeks since birth (assuming first weight is birth)
                  if (processedWeights.length > 0) {
                    const birthDate = new Date(processedWeights[0].x);
                    const currentDate = new Date(value);
                    const weeksDiff = Math.floor((currentDate - birthDate) / (1000 * 3600 * 24 * 7));
                    return `${weeksDiff} sem`;
                  }
                  return "";
                }}
              />
              <YAxis
                tick={{ fill: isDarkMode ? "#94a3b8" : "#475569", fontSize: "12px" }}
                axisLine={false}
                tickLine={false}
                domain={yAxisDomain}
                ticks={yAxisTicks}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: "transparent" }}
              />
              <Line
                data={processedWeights}
                dataKey="y"
                label="Poids"
                type="basis"
                dot={false}
                stroke="#f59e0b"
                fill="url(#colorValue)"
                strokeWidth={2}
                radius={[7, 7, 0, 0]}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="flex justify-end gap-2">
          {GMQ !== 0 && (
            <span
              className={
                "rounded-md border p-1 text-sm font-semibold " +
                (GMQ < 0.75
                  ? "border-rose-600 bg-rose-50 text-rose-700 dark:border-rose-500 dark:bg-rose-900/30 dark:text-rose-300"
                  : "border-emerald-600 bg-emerald-50 text-emerald-700 dark:border-emerald-500 dark:bg-emerald-900/30 dark:text-emerald-300")
              }
            >
              {"GMQ moyen : " + GMQ.toFixed(3) + " kg/jour"}
            </span>
          )}
        </div>
      </div>
    </Fragment>
  );
}

export default WeightChart;
